<template>
  <v-card>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Loading...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-form @submit.prevent="true" v-model="valid" autocomplete="off">
      <v-container class="pt-3 pb-0" v-if="!showResults">
        <h3>Bulk Add {{ title }}</h3>

        <v-alert dense type="error" v-if="showAlert">
          The uploaded file could not be processed for the selected configuration
        </v-alert>

        <em>Please upload a file</em>
        <p>
          <a v-if="instructions" href="#" @click.prevent="showInstructions = true"
          class="black-link"><strong>Check {{ title }} CSV rules</strong></a
          >
        </p>
        <v-row class="mt-3">
          <v-col cols="12">
            <v-file-input
              label="Choose file"
              autofocus
              clearable
              show-size
              :rules="rules"
              accept=".csv"
              persistent-hint
              v-model="file"
              hint="Please upload a valid .csv file"
            >
            </v-file-input>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="pt-3 pb-0" v-if="showResults">
        <h3>Bulk Add Results</h3>
        <p class="my-0" v-if="errors.length">
          <em>Rows successfully added: {{ insertedCount }}</em>
        </p>
        <p class="my-0" v-if="errors.length">
          <em>Rows with errors: {{ errors.length }}</em>
        </p>
        <v-alert type="success" v-if="!errors.length">
          <p>All rows added successfully</p>
          <p>
            <em>Total: {{ insertedCount }}</em>
          </p>
        </v-alert>
        <v-row dense v-if="errors.length">
          <v-col>
            <v-btn color="red" class="white--text" @click="downloadErrors()" small
              ><em
                ><strong>Download {{ title }} errors</strong></em
              ></v-btn
            >
            <v-data-table dense :headers="errorHeaders" :items="errors">
              <template v-slot:item="props">
                <tr>
                  <td>{{ props.item }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="!showResults" color="success" :disabled="!valid" @click="upload" :loading="loading">
          Upload
        </v-btn>
        <v-btn @click="close">Close</v-btn>
      </v-card-actions>
    </v-form>
    <toaster ref="toaster"></toaster>
    <v-dialog v-if="instructions" persistent v-model="showInstructions" max-width="800">
      <v-card>
        <v-container>
          <h3>{{ title }} CSV rules</h3>
          <h4>Total columns: {{ instructions.columns.length }}</h4>
          <h4>Include Headers: {{ instructions.addHeaders ? 'Yes' : 'No' }}</h4>

          <v-row class="mt-2">
            <v-btn color="green" class="ml-3 white--text" @click="downloadTemplate()" small
              ><em
                ><strong>Download {{ title }} template</strong></em
              ></v-btn
            >
          </v-row>

          <v-row>
            <v-col>{{ instructions.notes }}</v-col>
          </v-row>
          <v-timeline dense side="end">
            <v-timeline-item v-for="(column, i) in instructions.columns" :key="column.name" fill-dot dot-color="blue" large>
              <template v-slot:icon>
                <strong
                  ><span class="white--text">#{{ i + 1 }}</span></strong
                >
              </template>
              <v-card>
                <v-card-title>{{ column.name }}<strong v-if="column.required" class="text-error">*</strong></v-card-title>
                <v-card-text v-if="column.description">{{ column.description }}</v-card-text>
                <v-card-text v-if="column.acceptedValues">
                  <h5>Accepted values:</h5>
                  {{ column.acceptedValues.join(', ') }}
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showInstructions = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Toaster from '@/components/Toaster'
import AdminService from '@/services/admin.service'
import { TaskListTypes, ChangeTypes, ApprovalTypes } from '@/common/types'
import Papa from 'papaparse'
import downloadFile from '@/common/downloadFile'

export default {
  name: 'BulkUploadCsvFile',
  components: {
    Toaster
  },
  props: {
    communityAbbreviation: String,
    configurationName: String
  },
  data() {
    return {
      file: [],
      valid: false,
      loading: false,
      errors: [],
      showResults: false,
      showAlert: false,
      insertedCount: 0,
      errorsText: '',
      errorHeaders: [
        {
          text: 'Error',
          sortable: false,
          value: ''
        }
      ],
      rules: [v => ((v && v.size) || 'Required'), value => value.size < 1000000 || 'Size should be less than 1 MB'],
      showInstructions: false,
      instructions: getInstructions(this.configurationName)
    }
  },
  computed: {
    title() {
      switch (this.configurationName) {
        case 'tasks':
          return 'Tasks'
        case 'mocType':
          return 'MOC Types'
        case 'changeCategory':
          return 'Change Categories'
        case 'approvalProcessType':
          return 'Approval Process Types'
        case 'approvalProcessTypeFA':
          return 'Approvers / FYIs to approval process types'
        case 'changeCategoryMocType':
          return 'Change Category MOC Types'
        case 'qa':
          return 'Fields and Responses'
        case 'qaRelationship':
          return 'Response Relationships'
        default:
          return ''
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    upload() {
      this.loading = true
      AdminService.uploadConfigurationFile(this.file, this.configurationName, this.communityAbbreviation)
        .then(response => {
          this.errors = response.item1
          this.errorsText = this.errors.join('\n')

          this.insertedCount = response.item2
          this.showResults = true
          this.showAlert = false
          if (this.insertedCount > 0) this.$emit('upload')
        })
        .catch(err => {
          this.showAlert = true
        })
        .finally(() => (this.loading = false))
    },
    downloadTemplate() {
      const csv = Papa.unparse({ fields: getInstructions(this.configurationName).columns.map(c => c.name) })
      downloadFile(csv, '.csv', `${this.title} import template.csv`)
    },
    downloadErrors() {
      const csv = Papa.unparse(this.errors.map(e => ({ Error: e })), { encoding: 'utf-8' })
      downloadFile(csv, '.csv', `${this.title} import errors - ${new Date().toLocaleString()}.csv`)
    }
  }
}

const getInstructions = configurationName => {
  switch (configurationName) {
    case 'tasks':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'Unique reference',
            required: true
          },
          {
            name: 'Task',
            required: true
          },
          {
            name: 'Required in order to',
            required: true,
            acceptedValues: Object.values(TaskListTypes.properties)
              .filter(x => x.key !== 'all')
              .map(x => x.value)
          },
          {
            name: 'Related change type',
            required: true,
            acceptedValues: Object.values(ChangeTypes.properties).map(x => x.value)
          },
          {
            name: 'Step',
            required: true
          },
          {
            name: 'Operational Readiness Inspection',
            required: true,
            acceptedValues: ['Yes', 'No']
          },
          {
            name: 'Task Owner (email)'
          },
          {
            name: 'Task Assignee (email)'
          },
          {
            name: 'Documented Information Link',
            description: 'Each documented information separated by a semicolon(;)'
          },
          {
            name: 'Task Review Date'
          },
          {
            name: 'Task Review Notes'
          }
        ]
      }
    case 'mocType':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'Unique reference',
            required: true
          },
          {
            name: 'Name',
            required: true
          },
          {
            name: 'Enabled',
            required: true,
            acceptedValues: ['Yes', 'No']
          }
        ]
      }
    case 'changeCategory':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'Unique reference',
            required: true
          },
          {
            name: 'Name',
            required: true
          },
          {
            name: 'Facilitator (email)',
            required: true
          },
          {
            name: 'Facilitator Backup (email)'
          },
          {
            name: 'Enabled',
            acceptedValues: ['Yes', 'No']
          }
        ]
      }
    case 'approvalProcessType':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'Unique reference',
            required: true
          },
          {
            name: 'Name',
            required: true
          },
          {
            name: 'Approval Type',
            required: true,
            acceptedValues: Object.values(ApprovalTypes.properties).map(x => x.value)
          },
          {
            name: 'Approval Days'
          },
          {
            name: 'Owner (email)'
          },
          {
            name: 'Change Category Reference',
            required: true
          },
          {
            name: 'Line Manager (email)',
            required: true
          },
          {
            name: 'Senior Management (email)',
            required: true
          }
        ]
      }
    case 'changeCategoryMocType':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'MOC Type Reference',
            required: true
          },
          {
            name: 'Change Category Reference',
            description: 'Leave empty to associate the MOC type to all change categories'
          }
        ]
      }
    case 'qa':
      return {
        addHeaders: true,
        notes: 'Add the same columns as headers to indicate cascade fields (up to 5)',
        columns: [
          {
            name: 'Question unique reference',
            required: true,
            acceptedValues: [
              'cField10* or cField13* for single selects',
              'cField14* for multi selects',
              'every other cField* for text'
            ]
          },
          {
            name: 'Question',
            required: true
          },
          {
            name: 'Answer unique reference',
            required: true
          },
          {
            name: 'Answer',
            required: true
          }
        ]
      }
    case 'approvalProcessTypeFA':
      return {
        addHeaders: true,
        columns: [
          {
            name: 'Role',
            required: true,
            acceptedValues: ['Approver', 'FYI']
          },
          {
            name: 'User (email)',
            required: true
          },
          {
            name: 'Approval Process Type Reference',
            required: true
          }
        ]
      }
    case 'qaRelationship':
      return {
        addHeaders: true,
        notes:
          'Headers are reference to the field which the response reference belongs e.g cField101 (as header), 21 (response reference as column value), add other fields to the right as needed (up to 60)',
        columns: [
          {
            name: 'Task Reference'
          },
          {
            name: 'Approvers (email)',
            description: 'If more than one separate by semicolon (;)'
          },
          {
            name: 'FYIs (email)',
            description: 'If more than one separate by semicolon (;)'
          },
          {
            name: 'Field Reference (as header), Response Reference (as column value)'
          }
        ]
      }
  }
}
</script>
<style scoped>
.black-link {
  color: #000000;
}

</style>
